import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { DATE_FORMAT } from '@cyberint/common-utils';
import { Themes } from '../../style/theme-consts';
import Icons, { IconSizes } from '../Icons';
import Modal from '../Modal';
import { ModalModes } from '../Modal/modal-consts';
import Select from '../Select/Select';
import DateRangePicker from './DateRangePicker';
import { CUSTOM_RANGE, mapDateOptionToRange } from './consts';
import * as S from './styled';

const MODAL_SIZE = { width: '500px', height: '580px' };

const DateRangeSelect = ({
  value,
  onChange,
  onOpen,
  onClose,
  options,
  dateFormat = DATE_FORMAT.MMM_DD_YYYY_FORMAT,
  addCustomOption = true,
  width,
  height,
  label,
  modalApplyLabel,
  modalCancelLabel,
  testId,
  mode
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [rangeValue, setRangeValue] = useState(value);

  useEffect(() => {
    setRangeValue(value);
  }, [value]);

  const onRangeChange = ([from, to]) => {
    setRangeValue({ from, to, option: CUSTOM_RANGE });
  };

  const handleChange = e => {
    const selected = e.target.value;
    if (selected === CUSTOM_RANGE.value) {
      setModalOpen(true);
      return;
    }
    const mappedValue = mapDateOptionToRange(selected, options);
    onChange(mappedValue);
  };

  const onModalOk = () => {
    onChange(rangeValue);
    setModalOpen(false);
  };

  const selectedValue = value?.option?.value || value;

  // set Select value as 'any' (different from 'custom') to prevent marking it as selected and then not being able to click it again
  return (
    <>
      <Select
        className="dsc-date-range-select"
        mode={mode}
        onChange={handleChange}
        onOpen={onOpen}
        onClose={onClose}
        options={addCustomOption ? [...options, CUSTOM_RANGE] : options}
        value={selectedValue === CUSTOM_RANGE.value ? 'any' : selectedValue}
        width={width}
        height={height}
        label={label}
        testId={testId}
        renderValue={() => {
          const renderedVal =
            selectedValue === CUSTOM_RANGE.value
              ? `${value?.from && format(new Date(value.from), dateFormat)} - ${
                  value?.to && format(new Date(value.to), dateFormat)
                }`
              : options.find(o => o.value === selectedValue)?.label;
          return (
            renderedVal != null && (
              <S.SelectPlaceholder>
                <Icons.Calendar className="dsc-date-range-select-icon" fontSize={IconSizes.S} />
                <div>{renderedVal}</div>
              </S.SelectPlaceholder>
            )
          );
        }}
      />
      <Modal
        mode={ModalModes.LIGHT}
        isOpen={isModalOpen}
        testId={testId && `${testId}-RangeModal`}
        size={MODAL_SIZE}
        content={
          <S.DateRangePopperWrapper>
            <DateRangePicker
              testId={testId && `${testId}-RangePicker`}
              onChange={onRangeChange}
              dateFormat={dateFormat}
              value={[rangeValue?.from, rangeValue?.to]}
            />
          </S.DateRangePopperWrapper>
        }
        onClose={() => setModalOpen(false)}
        onOk={onModalOk}
        okText={modalApplyLabel || 'Apply'}
        cancelText={modalCancelLabel || 'Cancel'}
      />
    </>
  );
};

DateRangeSelect.propTypes = {
  /* provides selected value { from, to, option } */
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any.isRequired,
      unit: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired
    })
  ).isRequired,
  value: PropTypes.shape({
    from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    option: PropTypes.any
  }).isRequired,
  addCustomOption: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  modalApplyLabel: PropTypes.string,
  modalCancelLabel: PropTypes.string,
  dateFormat: PropTypes.string,
  testId: PropTypes.string,
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT])
};

export default DateRangeSelect;
